import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { SubDenialReason } from "../__generated__/graphql";
import { useAppealIdFromSession } from "./appeal-id-context";

export const GET_ELIGIBILITY_DATA = gql(`
  query GetEligibilityData($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      denial {
        condition
        medicationBrandName
        denialReason
        subDenialReasons
        bmiObesityCriteria
        bmiComorbidCriteria
      }
      isCoverageFund
    }
  }
`);

export type EligibilityState = {
  condition: string | null;
  medication: string | null;
  denialReason: string | null;
  subDenialReasons: SubDenialReason[];
  bmiObesityCriteria: number | null;
  bmiComorbidCriteria: number | null;
  coverageFundAppeal: boolean;
};

type EligibilityContextValue = {
  eligibility: EligibilityState;
  setEligibility: Dispatch<SetStateAction<EligibilityState>>;
  loading: boolean;
};

type EligibilityProviderProps = {
  children: ReactNode;
};

const EligibilityContext = createContext<EligibilityContextValue | null>(null);

export const EligibilityProvider = ({ children }: EligibilityProviderProps) => {
  const [eligibility, setEligibility] = useState<EligibilityState>({
    condition: null,
    medication: null,
    denialReason: null,
    subDenialReasons: [],
    bmiObesityCriteria: null,
    bmiComorbidCriteria: null,
    coverageFundAppeal: false,
  });

  const { appealId } = useAppealIdFromSession();

  const [getEligibilityData, { data: appealData, loading }] = useLazyQuery(
    GET_ELIGIBILITY_DATA,
    {
      fetchPolicy: "no-cache",
    },
  );

  // Execute query when appealId becomes available
  useEffect(() => {
    if (appealId) {
      getEligibilityData({
        variables: { appealId },
      });
    }
  }, [appealId]);

  const finalEligibility = useMemo(() => {
    if (appealData?.getAppealById?.denial) {
      const denial = appealData.getAppealById.denial;
      return {
        condition: denial.condition || eligibility.condition,
        medication: denial.medicationBrandName || eligibility.medication,
        denialReason: denial.denialReason || eligibility.denialReason,
        subDenialReasons:
          denial.subDenialReasons.length > 0
            ? denial.subDenialReasons
            : eligibility.subDenialReasons,
        bmiObesityCriteria:
          denial.bmiObesityCriteria ?? eligibility.bmiObesityCriteria,
        bmiComorbidCriteria:
          denial.bmiComorbidCriteria ?? eligibility.bmiComorbidCriteria,
        coverageFundAppeal:
          appealData.getAppealById.isCoverageFund ??
          eligibility.coverageFundAppeal,
      };
    }
    return eligibility;
  }, [appealData, eligibility]);

  const value = useMemo(
    () => ({
      eligibility: finalEligibility,
      setEligibility,
      loading,
    }),
    [finalEligibility, loading],
  );

  return (
    <EligibilityContext.Provider value={value}>
      {children}
    </EligibilityContext.Provider>
  );
};

export const useEligibility = () => {
  const eligibilityContext = useContext(EligibilityContext);

  if (eligibilityContext === null) {
    throw new Error(
      "useEligibility must be used within an EligibilityProvider",
    );
  }

  return eligibilityContext;
};
